import { useEffect } from 'react';

// Adds JobPosting Schema Markup (Google for Jobs) + SEO title/description for a job.
export const useJobPostingSchema = (job) => {
  useEffect(() => {
    if (!job) return;

    const LOGO = "https://ineedasocialworker.com/inasw-logo-crisp.png";
    const datePosted = job.created_at ? new Date(job.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

    // validThrough: explicit closing date, else 60 days after posting
    let validThrough = job.application_deadline || job.closing_date || job.expiration_date || null;
    if (!validThrough && job.created_at) {
      const d = new Date(job.created_at);
      d.setDate(d.getDate() + 60);
      validThrough = d.toISOString().split('T')[0];
    }

    const applyUrl = job.external_url || job.source_url || `https://ineedasocialworker.com/jobs/${job.job_id}`;

    const jobPostingSchema = {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      "title": job.title,
      "description": job.description,
      "datePosted": datePosted,
      "identifier": {
        "@type": "PropertyValue",
        "name": job.company_name || "INeedASocialWorker",
        "value": job.job_id
      },
      "hiringOrganization": {
        "@type": "Organization",
        "name": job.company_name || "Employer",
        "sameAs": job.company_website || "https://ineedasocialworker.com",
        "logo": job.company_logo || LOGO
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": job.location ? job.location.split(',')[0]?.trim() : "",
          "addressRegion": job.location ? job.location.split(',')[1]?.trim() : "",
          "addressCountry": "US"
        }
      },
      "employmentType": job.job_type ? job.job_type.toUpperCase().replace('-', '_') : "FULL_TIME",
      "industry": "Healthcare, Social Services",
      "occupationalCategory": "21-1021.00 - Child, Family, and School Social Workers",
      "qualifications": job.license_required ? `${job.license_required} license required` : "Social Work License required",
      "url": applyUrl,
      "directApply": job.is_external ? false : (job.can_apply_on_platform !== false)
    };

    if (validThrough) {
      jobPostingSchema.validThrough = validThrough;
    }

    if (job.salary_min || job.salary_max) {
      jobPostingSchema.baseSalary = {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": {
          "@type": "QuantitativeValue",
          "unitText": "YEAR"
        }
      };
      if (job.salary_min && job.salary_max) {
        jobPostingSchema.baseSalary.value.minValue = job.salary_min;
        jobPostingSchema.baseSalary.value.maxValue = job.salary_max;
      } else if (job.salary_min) {
        jobPostingSchema.baseSalary.value.value = job.salary_min;
      } else if (job.salary_max) {
        jobPostingSchema.baseSalary.value.value = job.salary_max;
      }
    }

    if (job.is_remote) {
      jobPostingSchema.jobLocationType = "TELECOMMUTE";
    }
    jobPostingSchema.applicantLocationRequirements = {
      "@type": "Country",
      "name": "United States"
    };

    // Do not emit structured data for jobs that are no longer active
    const existingBefore = document.getElementById('job-posting-schema');
    if (job.status && job.status !== 'active') {
      if (existingBefore) existingBefore.remove();
      return;
    }

    let scriptTag = document.getElementById('job-posting-schema');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'job-posting-schema';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(jobPostingSchema);

    document.title = `${job.title} at ${job.company_name} | INeedASocialWorker.com`;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = `Apply for ${job.title} at ${job.company_name} in ${job.location}. ${job.license_required ? `${job.license_required} license required.` : ''} ${job.is_remote ? 'Remote work available.' : ''} Find social work jobs on INeedASocialWorker.com.`;

    return () => {
      const existingScript = document.getElementById('job-posting-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [job]);
};
